/**
 * Titles React Query Hooks
 * Job Titles and Relationship Types
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { titleService } from '../services';

const JOB_TITLES_KEY = 'jobTitles';
const RELATIONSHIP_TYPES_KEY = 'relationshipTypes';

// ============================================================================
// JOB TITLES
// ============================================================================

export function useJobTitles(includeInactive = false) {
  return useQuery({
    queryKey: [JOB_TITLES_KEY, includeInactive],
    queryFn: () => titleService.getJobTitles(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateJobTitle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.createJobTitle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_TITLES_KEY] });
    },
  });
}

export function useUpdateJobTitle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => titleService.updateJobTitle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_TITLES_KEY] });
    },
  });
}

export function useDeleteJobTitle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.deleteJobTitle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_TITLES_KEY] });
    },
  });
}

export function useReorderJobTitles() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.reorderJobTitles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_TITLES_KEY] });
    },
  });
}

export function useSeedJobTitles() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.seedDefaultJobTitles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_TITLES_KEY] });
    },
  });
}

// ============================================================================
// RELATIONSHIP TYPES
// ============================================================================

export function useRelationshipTypes(includeInactive = false) {
  return useQuery({
    queryKey: [RELATIONSHIP_TYPES_KEY, includeInactive],
    queryFn: () => titleService.getRelationshipTypes(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateRelationshipType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.createRelationshipType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_TYPES_KEY] });
    },
  });
}

export function useUpdateRelationshipType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => titleService.updateRelationshipType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_TYPES_KEY] });
    },
  });
}

export function useDeleteRelationshipType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.deleteRelationshipType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_TYPES_KEY] });
    },
  });
}

export function useReorderRelationshipTypes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.reorderRelationshipTypes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_TYPES_KEY] });
    },
  });
}

export function useSeedRelationshipTypes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: titleService.seedDefaultRelationshipTypes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RELATIONSHIP_TYPES_KEY] });
    },
  });
}
